import React, { useState, useEffect } from 'react';
import './PrivacyConsent.css';
import { getFrontendConfig } from '../config/environment';

interface PrivacyConsentProps {
  onConsentChange?: (consent: ConsentSettings) => void;
}

interface ConsentSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const PrivacyConsent: React.FC<PrivacyConsentProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
  const [consent, setConsent] = useState<ConsentSettings>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const config = getFrontendConfig();

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem('privacy-consent');
    if (!savedConsent) {
      // Show banner for new visitors
      setShowBanner(true);
    } else {
      // Load saved consent settings
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
        onConsentChange?.(parsedConsent);
        
        // Apply existing consent to Matomo
        if (config.matomo.enabled && typeof window !== 'undefined' && (window as any)._paq) {
          if (parsedConsent.analytics) {
            (window as any)._paq.push(['setConsentGiven']);
            (window as any)._paq.push(['trackPageView']);
            (window as any)._paq.push(['enableLinkTracking']);
          } else {
            (window as any)._paq.push(['setConsentWithdrawn']);
          }
        }
      } catch (error) {
        console.error('Error parsing saved consent:', error);
        setShowBanner(true);
      }
    }
  }, [onConsentChange, config.matomo.enabled]);

  // Track scroll position to hide/show privacy icon
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolledFromTop(scrollTop > 100); // Show icon after scrolling 100px
    };

    // Set initial state
    handleScroll();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const saveConsent = (consentSettings: ConsentSettings) => {
    localStorage.setItem('privacy-consent', JSON.stringify(consentSettings));
    localStorage.setItem('privacy-consent-date', new Date().toISOString());
    setConsent(consentSettings);
    onConsentChange?.(consentSettings);

    // Handle Matomo analytics based on consent
    if (config.matomo.enabled && typeof window !== 'undefined' && (window as any)._paq) {
      if (consentSettings.analytics) {
        // User has consented to analytics - enable tracking
        (window as any)._paq.push(['setConsentGiven']);
        (window as any)._paq.push(['trackPageView']);
        (window as any)._paq.push(['enableLinkTracking']);
        console.log('📊 Analytics enabled by user consent');
      } else {
        // User has not consented - disable tracking
        (window as any)._paq.push(['setConsentWithdrawn']);
        console.log('📊 Analytics disabled by user consent');
      }
    }
  };

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    saveConsent(allConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    saveConsent(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleCustomSave = () => {
    saveConsent(consent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleConsentChange = (type: keyof ConsentSettings, value: boolean) => {
    if (type === 'necessary') return; // Necessary cookies cannot be disabled
    setConsent(prev => ({ ...prev, [type]: value }));
  };

  const openSettings = () => {
    setShowSettings(true);
    setShowBanner(false);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  return (
    <>
      {/* Privacy Settings Icon */}
      <button
        className={`privacy-icon ${isScrolledFromTop ? 'visible' : 'hidden'}`}
        onClick={openSettings}
        title="Datenschutz-Einstellungen"
        aria-label="Datenschutz-Einstellungen öffnen"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm0 2.09L18 6.09v5c0 3.74-2.55 7.26-6 8.27-3.45-1.01-6-4.53-6-8.27v-5l6-2z"/>
          <path d="M10.5 12.5l1.5 1.5 3-3-1.41-1.41L11.5 11.67l-.59-.58L10.5 12.5z"/>
        </svg>
      </button>

      {/* Consent Banner */}
      {showBanner && (
        <div className="privacy-banner">
          <div className="privacy-banner-content">
            <div className="privacy-banner-text">
              <h3>Datenschutz & Cookies</h3>
              <p>
                Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                Einige sind notwendig für den Betrieb der Website, während andere uns helfen, die Website zu verbessern und Ihnen 
                personalisierte Inhalte anzuzeigen.
              </p>
            </div>
            <div className="privacy-banner-actions">
              <button 
                className="privacy-btn privacy-btn-secondary"
                onClick={handleAcceptNecessary}
              >
                Nur Notwendige
              </button>
              <button 
                className="privacy-btn privacy-btn-tertiary"
                onClick={openSettings}
              >
                Einstellungen
              </button>
              <button 
                className="privacy-btn privacy-btn-primary"
                onClick={handleAcceptAll}
              >
                Alle Akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="privacy-modal-overlay" onClick={closeSettings}>
          <div className="privacy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="privacy-modal-header">
              <h2>Datenschutz-Einstellungen</h2>
              <button 
                className="privacy-modal-close"
                onClick={closeSettings}
                aria-label="Schließen"
              >
                ×
              </button>
            </div>
            
            <div className="privacy-modal-content">
              <p className="privacy-modal-description">
                Wir respektieren Ihre Privatsphäre und geben Ihnen die Kontrolle über Ihre Daten. 
                Sie können Ihre Einstellungen jederzeit über das Datenschutz-Symbol unten links ändern.
              </p>

              <div className="privacy-settings">
                <div className="privacy-setting">
                  <div className="privacy-setting-info">
                    <h4>Notwendige Cookies</h4>
                    <p>Diese Cookies sind für das Funktionieren der Website erforderlich und können nicht deaktiviert werden.</p>
                  </div>
                  <div className="privacy-toggle">
                    <input
                      type="checkbox"
                      id="necessary"
                      checked={consent.necessary}
                      disabled
                      onChange={() => {}}
                    />
                    <label htmlFor="necessary" className="toggle-label disabled">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="privacy-setting">
                  <div className="privacy-setting-info">
                    <h4>Analyse & Performance</h4>
                    <p>Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, um die Benutzererfahrung zu verbessern. Wir verwenden Matomo, eine datenschutzfreundliche Analytics-Lösung.</p>
                  </div>
                  <div className="privacy-toggle">
                    <input
                      type="checkbox"
                      id="analytics"
                      checked={consent.analytics}
                      onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                    />
                    <label htmlFor="analytics" className="toggle-label">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="privacy-setting">
                  <div className="privacy-setting-info">
                    <h4>Marketing & Werbung</h4>
                    <p>Diese Cookies werden verwendet, um Ihnen relevante Werbung und personalisierte Inhalte anzuzeigen.</p>
                  </div>
                  <div className="privacy-toggle">
                    <input
                      type="checkbox"
                      id="marketing"
                      checked={consent.marketing}
                      onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                    />
                    <label htmlFor="marketing" className="toggle-label">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="privacy-setting">
                  <div className="privacy-setting-info">
                    <h4>Präferenzen</h4>
                    <p>Diese Cookies speichern Ihre Einstellungen und Präferenzen für zukünftige Besuche.</p>
                  </div>
                  <div className="privacy-toggle">
                    <input
                      type="checkbox"
                      id="preferences"
                      checked={consent.preferences}
                      onChange={(e) => handleConsentChange('preferences', e.target.checked)}
                    />
                    <label htmlFor="preferences" className="toggle-label">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="privacy-modal-footer">
                <button 
                  className="privacy-btn privacy-btn-secondary"
                  onClick={handleAcceptNecessary}
                >
                  Nur Notwendige
                </button>
                <button 
                  className="privacy-btn privacy-btn-primary"
                  onClick={handleCustomSave}
                >
                  Einstellungen Speichern
                </button>
              </div>

              <div className="privacy-links">
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                  Datenschutzerklärung
                </a>
                <a href="/impressum" target="_blank" rel="noopener noreferrer">
                  Impressum
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrivacyConsent;
